// Disable HMR for this plugin to prevent duplicate identifier conflicts
import axios from 'axios'
import { defineRule, configure as _configure } from 'vee-validate'
import dayjs from 'dayjs'
import { parsePhoneNumber, ParseError as _ParseError } from 'libphonenumber-js'
import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app'

import {
  emailRegex,
  urlRegex,
  googleTagManagerIdRegex,
  googleAnalyticsIdRegex,
  facebookPixelIdRegex,
  cssLengthRegex,
  facebookUrlRegex,
  facebookEventUrlRegex,
  instagramUsernameRegex,
  twitterUsernameRegex,
  snapchatAccountRegex,
  youtubeChannelUrlRegex,
  youtubeUrlRegex,
  zoomUrlRegex,
  spotifyTrackUrlRegex,
  spotifyArtistUrlRegex,
  spotifyPlaylistUrlRegex,
  spotifyAlbumUrlRegex,
  vimeoUrlRegex,
  linkedinRegex,
  tiktokRegex,
  twitchRegex,
  spotifyPodcastUrlRegex,
  spotifyPodcasterUrlRegex,
  alphanumericRegex,
  alphanumericSpaceRegex,
} from '@/utils/regex/'

if (import.meta.hot) {
  import.meta.hot.decline()
}

export default defineNuxtPlugin((nuxtApp) => {
  // More robust HMR protection
  if (nuxtApp.$veeValidateRegistered || (import.meta.client && window.__VEE_VALIDATE_PLUGIN_LOADED)) {
    return
  }

  // Clear any existing vee-validate global state immediately to prevent identifier conflicts
  if (import.meta.client && window) {
    try {
      // Clear any global validate function or identifier that might cause conflicts
      if (window.validate) {
        delete window.validate
      }
      // Clear any vee-validate module variables that might be cached
      if (window.__vee_validate_rules) {
        delete window.__vee_validate_rules
      }
      if (window.__vee_validate_locale) {
        delete window.__vee_validate_locale
      }
      // Clear any potential duplicate identifier references
      if (window.defineRule) {
        delete window.defineRule
      }
    }
    catch (_e) {
      // Ignore cleanup errors during HMR
    }
  }

  // Mark as registered in both places
  nuxtApp.$veeValidateRegistered = true
  if (import.meta.client && window) {
    window.__VEE_VALIDATE_PLUGIN_LOADED = true
  }

  // Get runtime config safely
  const config = useRuntimeConfig()

  // Clear any existing vee-validate global state to prevent conflicts
  if (import.meta.client && window) {
    try {
      // Clear vee-validate's internal registry
      if (window.__vee_validate_rules) {
        delete window.__vee_validate_rules
      }
      if (window.__vee_validate_locale) {
        delete window.__vee_validate_locale
      }
      // Clear any validate function that might be in global scope
      if (window.validate && typeof window.validate === 'function') {
        delete window.validate
      }
    }
    catch (_e) {
      // Ignore cleanup errors
    }
  }

  // Enhanced wrapper that completely prevents duplicate registration errors and identifier conflicts
  const safeDefineRule = (name, rule) => {
    try {
      // Extra HMR protection: check if we're in an HMR context and prevent duplicate rules
      if (import.meta.client && import.meta.hot && window.__VEE_VALIDATE_RULES_CACHE) {
        const ruleId = `${name}_${Date.now()}_${Math.random()}`
        if (window.__VEE_VALIDATE_RULES_CACHE.has(name)) {
          return // Skip if rule already exists in cache
        }
        window.__VEE_VALIDATE_RULES_CACHE.set(name, ruleId)
      }

      defineRule(name, rule)
    }
    catch (error) {
      // Don't log anything in development for HMR-related errors
      if (import.meta.dev && error.message && (
        error.message.includes('already')
        || error.message.includes('duplicate')
        || error.message.includes('has already been declared')
        || error.message.includes('Identifier')
        || error.message.includes('SyntaxError')
      )) {
        return // Silently ignore HMR conflicts
      }
      if (import.meta.dev) {
        console.warn(`Rule registration warning for '${name}':`, error.message)
      }
    }
  }

  // Initialize HMR rule cache
  if (import.meta.client && import.meta.hot && !window.__VEE_VALIDATE_RULES_CACHE) {
    window.__VEE_VALIDATE_RULES_CACHE = new Map()
  }

  safeDefineRule('required', (value) => {
    if (!!value && value.length > 0) {
      return true
    }
    return 'This field is required'
  })

  safeDefineRule('googleTagManagerId', (value) => {
    if (!!value && value.length > 0 && googleTagManagerIdRegex.test(value)) {
      return true
    }
    return 'Google Tag Manager id is invalid'
  })

  safeDefineRule('googleAnalyticsId', (value) => {
    if (!!value && value.length > 0 && googleAnalyticsIdRegex.test(value)) {
      return true
    }
    return 'Google Analytics id is invalid'
  })

  safeDefineRule('facebookPixelId', (value) => {
    if (!!value && value.length > 0 && facebookPixelIdRegex.test(value)) {
      return true
    }
    return 'Facebook Pixel id is invalid'
  })

  safeDefineRule('alphanumeric', (value) => {
    if (!!value && value.length > 0 && alphanumericRegex.test(value)) {
      return true
    }
    return 'Input may consist of alphanumeric characters only'
  })

  safeDefineRule('alphanumericSpace', (value) => {
    if (!!value && value.length > 0 && alphanumericSpaceRegex.test(value)) {
      return true
    }
    return 'May consist of upper and lower-case letters, numbers 0 through 9, and the space character only.'
  })

  safeDefineRule('mobileNumber', (value) => {
    const invalidMessage = 'Please enter a valid mobile number'
    try {
      if (!value || value.length === 0) {
        throw invalidMessage
      }
      const phoneNumber = parsePhoneNumber(value)
      // We don't want the library change the number and say it's valid
      // e.g: Change "+6101234567" to "+611234567" and say it's valid lol.
      if (phoneNumber.isValid() && phoneNumber.number === value) {
        return true
      }
      else {
        throw invalidMessage
      }
    }
    catch (error) {
      // If you need more sophisticated error handling, follow this pattern
      // if (error instanceof ParseError) {
      //   return false;
      // }
      return invalidMessage
    }
  })

  safeDefineRule('date-of-birth', (value) => {
    if (!!value && value.length > 0) {
      const [year, month, day] = value.split('-')

      if (year && month && day) {
        return true
      }
    }

    return 'Please complete date of birth'
  })

  safeDefineRule('futureTime', (value, field) => {
    if (!!value && value.length > 0 && dayjs(value).isAfter(new Date())) {
      return true
    }
    return `The ${field} must be in the future`
  })

  /*
  safeDefineRule('notBeforeTime', {
    getMessage: (field, [_, compareTimeName]) => `The ${field} cannot be before the ${compareTimeName}`,
    validate: (value, { compareTime }) => {
      return dayjs(value).isAfter(new dayjs(compareTime));
    },
  }, { paramNames: ['compareTime', 'compareTimeName'] });
  */

  safeDefineRule('notBeforeTime', (value, field, [_, compareTimeName], compareTime) => {
    if (!!value && value.length > 0 && dayjs(value).isAfter(new dayjs(compareTime))) {
      return true
    }
    return `The ${field} cannot be before the ${compareTimeName}`
  })

  /*
  safeDefineRule('tzFormat', {
    getMessage: (field) => `Please complete ${field}`,
    validate: (value) => {
      return dayjs.utc(value)._isValid;
    },
  }); */

  safeDefineRule('tzFormat', (value, field) => {
    if (!!value && value.length > 0 && dayjs.utc(value)._isValid) {
      return true
    }
    return `Please complete ${field}`
  })

  safeDefineRule('emailAddresses', (value) => {
    const invalidMessage = 'Please make sure every email address is valid'
    if (!!value && value.length > 0) {
      const emails = value.replace(/\s/g, '').split(',')
      for (let email of emails) {
        if (!emailRegex.test(email)) {
          return invalidMessage
        }
      }
      return true
    }

    return invalidMessage
  })

  safeDefineRule('url', (value) => {
    if (!!value && value.length > 0 && urlRegex.test(value)) {
      return true
    }
    return 'URL is not valid'
  })

  safeDefineRule('maxTestEmailRecipients', (value, { max }) => {
    if (!!value && value.length > 0 && (value.split(',').length <= max)) {
      return true
    }
    return `You cannot send test emails to more than ${max} recipients`
  })

  safeDefineRule('arPrivacyPortalSubdomain', async (value, { currentSubdomain }) => {
    const invalidMessage = 'This subdomain is already in use, please edit to specify a different subdomain'
    if (!!value && value.length > 0) {
      if (value === currentSubdomain) {
        return true
      }
      try {
        const { status } = await axios.get(`https://${value}.${config.public.arPrivacyPortalDomain}`)
        if (status === 200) {
          return invalidMessage
        }
        return true
      }
      catch (error) {
        return true
      }
    }
    return invalidMessage
  })

  safeDefineRule('arCampaignSlug', async (value, { currentSlug, prefix }) => {
    const invalidMessage = 'This URL is already in use, please edit to specify a different URL'
    if (!!value && value.length > 0) {
      try {
        if (value === currentSlug) {
          return true
        }
        const { status } = await axios.get(`${config.public.arCampaignApiDomain}${config.public.arCampaignApiBaseUriPrefix}/campaign-uri?$filter=uri=${prefix}${value}`)
        if (status === 200) {
          return invalidMessage
        }
        return true
      }
      catch (e) {
        console.error(e)
        return true
      }
    }
    return invalidMessage
  })

  safeDefineRule('arPasswordLength', (value) => {
    if (!!value && value.length > 0 && value.length >= 8) {
      return true
    }
    return 'Password must be at least 8 characters'
  })

  safeDefineRule('uploadingAssets', (value) => {
    if (value) {
      return true
    }
    return 'Image is still being uploaded'
  })

  safeDefineRule('matchPassword', (value, args) => {
    if (!!value && value.length > 0 && !!args && args.length > 0 && value === args[0]) {
      return true
    }
    return 'Your passwords do not match, please try again'
  })

  safeDefineRule('cssWidth', (value) => {
    if (!!value && value.length > 0 && cssLengthRegex.test(value)) {
      return true
    }
    return 'This is not valid css length'
  })

  safeDefineRule('facebookUrl', (value) => {
    if (!!value && value.length > 0 && facebookUrlRegex.test(value)) {
      return true
    }
    return 'Facebook url is not valid'
  })

  safeDefineRule('facebookEventUrl', (value) => {
    if (!!value && value.length > 0 && facebookEventUrlRegex.test(value)) {
      return true
    }
    return 'Facebook Event url is not valid'
  })

  safeDefineRule('instagramUsername', (value) => {
    if (!!value && value.length > 0 && instagramUsernameRegex.test(value)) {
      return true
    }
    return 'Instagram username is not valid'
  })

  safeDefineRule('twitterUsername', (value) => {
    if (!!value && value.length > 0 && twitterUsernameRegex.test(value)) {
      return true
    }
    return 'Twitter username is not valid'
  })

  safeDefineRule('snapchatAccount', (value) => {
    if (!!value && value.length > 0 && snapchatAccountRegex.test(value)) {
      return true
    }
    return 'Snapchat account is not valid'
  })

  safeDefineRule('youtubeChannelUrl', (value) => {
    if (!!value && value.length > 0 && youtubeChannelUrlRegex.test(value)) {
      return true
    }
    return 'Youtube url is not valid'
  })

  safeDefineRule('youtubeUrl', (value) => {
    if (!!value && value.length > 0 && youtubeUrlRegex.test(value)) {
      return true
    }
    return 'Youtube url is not valid'
  })

  safeDefineRule('zoomUrl', (value) => {
    if (!!value && value.length > 0 && zoomUrlRegex.test(value)) {
      return true
    }
    return 'Zoom url is not valid'
  })

  safeDefineRule('spotifyTrackUrl', (value) => {
    if (!!value && value.length > 0 && spotifyTrackUrlRegex.test(value)) {
      return true
    }
    return 'Spotify Track url is not valid'
  })

  safeDefineRule('spotifyArtistUrl', (value) => {
    if (!!value && value.length > 0 && spotifyArtistUrlRegex.test(value)) {
      return true
    }
    return 'Spotify Artist url is not valid'
  })

  safeDefineRule('spotifyPlaylistUrl', (value) => {
    if (!!value && value.length > 0 && spotifyPlaylistUrlRegex.test(value)) {
      return true
    }
    return 'Spotify Playlist url is not valid'
  })

  safeDefineRule('spotifyAlbumUrl', (value) => {
    if (!!value && value.length > 0 && spotifyAlbumUrlRegex.test(value)) {
      return true
    }
    return 'Spotify Album url is not valid'
  })

  safeDefineRule('spotifyPodcastUrl', (value) => {
    if (!!value && value.length > 0 && spotifyPodcastUrlRegex.test(value)) {
      return true
    }
    return 'Spotify Podcast url (Episode) is not valid'
  })

  safeDefineRule('spotifyPodcasterUrl', (value) => {
    if (!!value && value.length > 0 && spotifyPodcasterUrlRegex.test(value)) {
      return true
    }
    return 'Spotify Podcaster url (Show) is not valid'
  })

  safeDefineRule('vimeoUrl', (value) => {
    if (!!value && value.length > 0 && vimeoUrlRegex.test(value)) {
      return true
    }
    return 'Vimeo url is not valid'
  })

  safeDefineRule('linkedinURL', (value) => {
    if (!!value && value.length > 0 && linkedinRegex.test(value)) {
      return true
    }
    return 'Linkedin URL is not valid'
  })

  safeDefineRule('tiktokUsername', (value) => {
    if (!!value && value.length > 0 && tiktokRegex.test(value)) {
      return true
    }
    return 'TikTok Username is not valid'
  })

  safeDefineRule('twitchUrl', (value) => {
    if (!!value && value.length > 0 && twitchRegex.test(value)) {
      return true
    }
    return 'Twitch URL is not valid'
  })

  safeDefineRule('date_format', (value, [format]) => {
    if (!value || value.length === 0) return true

    const isValid = dayjs(value, format, true).isValid()
    if (isValid) {
      return true
    }
    return `Date must be in ${format} format`
  })
})
